import { Option } from 'fp-ts/lib/Option';
import { FormEvent } from 'react';
import { isENTER } from '../../components/keycodes';
import { noop, tryNumber } from '../../util';
import { DIV, INPUT } from '../elements';

// This is an attempt to replace inputNumber from input.ts
// Previous component
// - sets defaultValue rather than value
// - may get undefined from getterValue
//
// Both behaviors may result in react issuing an "uncontrolled" component.
// After initiating an uncontrolled component, react has no access
// to it and cannot update its internal state when re-rendering the DOM.
//
// If the component was rendered before the local storage was initiated, react
// creates an uncontrolled component. When the local storage is updated,
// react cannot reset the updated value into the component.
//
// The original input is used at 35 places at time of writing,
// the proposition is to rename the old component and its uses and mark it
// as deprecated in its ts-doc.

export type InputAttributes = React.AllHTMLAttributes<HTMLInputElement> &
    React.RefAttributes<HTMLInputElement>;
export type AllInputAttributes = InputAttributes & React.Attributes;

export type Getter<T> = () => T;
export type Setter<T> = (v: T) => void;
export type Monitor = (v: string) => void;

export interface InputOptions<T, Attrs = AllInputAttributes> {
    key: string;
    get: Getter<T> | Getter<Option<T>>;
    // set takes undefined to force the programmer to deal with
    // the user setting the input to string ""
    // Option<T> might be a better type.
    set: Setter<T | undefined>;
    attrs: Attrs;
}

const isOption = (o: unknown): o is Option<unknown> =>
    o !== null &&
    typeof o === 'object' &&
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    typeof (o as any).toUndefined === 'function';

const getValue = <T>(get: Getter<T> | Getter<Option<T>>): T | '' => {
    const valueOrOption = get();
    const value = isOption(valueOrOption)
        ? valueOrOption.toUndefined()
        : valueOrOption;
    return value ?? '';
};

const checkAndSet =
    (set: Setter<number | undefined>, monitor: Monitor) =>
    (event: FormEvent<HTMLInputElement>) => {
        const targetValue = event.currentTarget.value;
        monitor(targetValue);
        if (targetValue === '') {
            set(undefined);
        } else if (event.currentTarget.checkValidity()) {
            tryNumber(targetValue).map(v => {
                set(v);
            });
        }
    };

/**
 * This component builds an input for numbers
 * @key should be unique in the form
 * @param monitor is triggered on any change of the input, regardless of input validity
 */
export const inputNumber = (
    { key, get, set, attrs }: InputOptions<number>,
    // monitor is moved out of the signature to separate it from HTML attributes
    // the default value is preferred to avoid dealing with optional parameters.
    monitor: Monitor = noop,
) => {
    // passing a null or undefined value to the react component
    // will create an uncontrolled component
    // string is needed to accept empty values
    const value = getValue<number | string>(get) ?? '';

    return DIV(
        'input__wrapper--number',
        INPUT({
            key,
            type: 'number',
            value: value,
            ...attrs,

            onChange: checkAndSet(set, monitor),
            onBlur: checkAndSet(set, monitor),
            onKeyUp: key => {
                if (isENTER(key)) {
                    checkAndSet(set, monitor)(key);
                }
            },
        }),
    );
};
